/**
 * PrintTechnologyPicker + PrintTechnologyChip (Phase 96): the per-product
 * Default / DTG / DTF pick on a Product Sync row.
 *
 * The pick is stored per (org, Shopify product) and stamped on the garments
 * at batch time; an order's own DTG/DTF request and a print floor's own
 * override still win over it. Default (no stored pick) leaves today's chain:
 * the SKU's own setting, then prefix rules, then the location default.
 *
 * Copies `ProductKindSwitch`'s editable / read-only split, gated on
 * permissions rather than the page's `admin` flag (`isAdmin()`), which would
 * refuse Warehouse Coordinators:
 *   - `skus.manage` (Admin, Super Admin, Coordinator; the write's own check)
 *     gets the SegmentedControl;
 *   - any other reader with `org_settings.read` reads the value as text;
 *   - a caller without `org_settings.read` gets nothing and sends no request.
 *
 * No dialog: the change is reversible and only reaches garments batched
 * after it. While a save runs the clicked value shows at once with a spinner
 * in that segment (`aria-busy`); the control stays enabled, so focus is never
 * dropped, and further clicks are ignored until it settles. A failed save puts
 * the stored value back (the cache was never touched) with a sticky toast.
 *
 * A product on a 3PL's blank (`product.supply`) renders neither the picker nor
 * the chip: the 3PL decides how its own garments print, and batch time ignores
 * a product pick on those lines.
 */
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info } from 'lucide-react';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Skeleton } from '@/components/ui/skeleton';
import { NoteLine } from '@/components/shared/NoteLine';
import { useRole } from '@/hooks/use-role';
import type { PreviewProduct } from '../lib/types';
import {
  usePrintTechMapped,
  useProductPrintTechnology,
  useSetProductPrintTechnology,
  type ProductPrintTechnology,
} from '../hooks/useProductPrintTechnologies';

type Choice = 'default' | ProductPrintTechnology;
/** Shown while the list loads: matches no option, so no segment reads as pressed. */
type Shown = Choice | 'loading';

const CHOICES: readonly Choice[] = ['default', 'DTG', 'DTF'];

const LABEL_KEY: Record<Choice, string> = {
  default: 'printTechnology.default',
  DTG: 'printTechnology.dtg',
  DTF: 'printTechnology.dtf',
};

type PrintTechnologyProduct = Pick<PreviewProduct, 'id' | 'title' | 'supply'>;

export interface PrintTechnologyPickerProps {
  product: PrintTechnologyProduct;
}

export function PrintTechnologyPicker({ product }: PrintTechnologyPickerProps) {
  if (product.supply) return null;
  return <PickerBody productId={product.id} productTitle={product.title} />;
}

function PickerBody({ productId, productTitle }: { productId: string; productTitle: string }) {
  const { t } = useTranslation('productSync');
  const { can } = useRole();
  const canRead = can('org_settings.read');
  const canEdit = can('skus.manage');
  const query = useProductPrintTechnology(productId);
  const mapped = usePrintTechMapped();
  const mutation = useSetProductPrintTechnology();
  const saving = mutation.isPending;

  if (!canRead) return null;

  if (query.isLoadingError) {
    return (
      <NoteLine icon={AlertTriangle} tone="warning">
        {t('printTechnology.loadError')}{' '}
        {/* aria-disabled, not disabled: a native disabled button would drop focus mid-retry. */}
        <button
          type="button"
          onClick={() => {
            if (!query.isFetching) void query.refetch();
          }}
          aria-disabled={query.isFetching || undefined}
          className="font-medium underline underline-offset-2 hover:text-amber-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-disabled:cursor-wait aria-disabled:opacity-50"
        >
          {t('printTechnology.retry')}
        </button>
      </NoteLine>
    );
  }

  const loading = query.data === undefined;
  const stored: Choice = query.data ?? 'default';
  const pending: Choice | null = saving ? (mutation.variables?.printTechnology ?? 'default') : null;
  const shown: Shown = loading ? 'loading' : (pending ?? stored);
  const label = (choice: Choice) => t(LABEL_KEY[choice]);

  const choose = (next: Shown) => {
    if (next === 'loading' || loading || saving || next === stored) return;
    mutation.mutate({ productId, printTechnology: next === 'default' ? null : next });
  };

  const status = loading ? t('printTechnology.loading') : saving ? t('printTechnology.saving') : '';

  return (
    <div className="flex flex-col gap-1.5" data-testid="print-technology-picker">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className="font-medium text-foreground">{t('printTechnology.label')}</span>
        {canEdit ? (
          <SegmentedControl<Shown>
            options={CHOICES.map((choice) => ({ value: choice, label: label(choice) }))}
            value={shown}
            onChange={choose}
            ariaLabel={t('printTechnology.groupAria', { name: productTitle })}
            disabled={loading}
            busyValue={pending ?? undefined}
            className={loading ? 'motion-safe:animate-pulse' : undefined}
          />
        ) : loading ? (
          <Skeleton className="h-4 w-12" />
        ) : (
          <span className="font-semibold text-foreground">{label(stored)}</span>
        )}
        <span className="min-w-0 break-words text-muted-foreground">{t('printTechnology.hintDefault')}</span>
      </div>
      <span role="status" className="sr-only">
        {status}
      </span>
      {canEdit && <NoteLine>{t('printTechnology.hintApplies')}</NoteLine>}
      {mapped && <NoteLine icon={Info}>{t('printTechnology.hintMapped')}</NoteLine>}
    </div>
  );
}

/**
 * The header chip: `DTG` or `DTF` when the product has a pick, nothing for
 * Default, while loading, or on a read error (the open row says so). Same
 * ring geometry as the row's Synced and Mapped chips, neutral like Mapped:
 * a setting, not a state.
 */
export function PrintTechnologyChip({ product }: { product: Pick<PreviewProduct, 'id' | 'supply'> }) {
  if (product.supply) return null;
  return <ChipBody productId={product.id} />;
}

function ChipBody({ productId }: { productId: string }) {
  const { t } = useTranslation('productSync');
  const { data } = useProductPrintTechnology(productId);
  if (!data) return null;
  const tech = t(LABEL_KEY[data]);
  return (
    <span
      className="hidden shrink-0 whitespace-nowrap rounded-md bg-background px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground ring-1 ring-border md:inline-flex"
      data-testid="print-technology-chip"
    >
      <span aria-hidden="true">{tech}</span>
      <span className="sr-only">{t('printTechnology.chipAria', { tech })}</span>
    </span>
  );
}
